import { Link, useNavigate } from "react-router-dom";
import { ChevronRight, Plus } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";
import { useAuthStore } from "@/store/auth";
import { formatDate } from "@/utils/formatDate";
import { useB2BInvoices, useMissingB2BInvoicePeriods } from "../hooks/useB2BInvoices";
import { B2BInvoiceStatusBadge } from "../components/B2BInvoiceStatusBadge";
import { MONTH_NAMES } from "../types";
import {
  B2B_BADGE_CHIP_CLASSES,
  B2B_BADGE_COLORS,
  B2B_BADGE_LABELS,
  B2B_EMPLOYEE_BADGES,
  type B2BEmployeeBadge,
} from "../badges";

function formatPln(value: string): string {
  const n = Number(value);
  if (Number.isNaN(n)) return value;
  return new Intl.NumberFormat("pl-PL", {
    style: "currency",
    currency: "PLN",
    minimumFractionDigits: 2,
    // "always" forces a thousands separator for 4-digit pl-PL values; the pl-PL
    // CLDR default (minimumGroupingDigits=2) otherwise skips grouping below 10 000.
    useGrouping: "always",
  }).format(n);
}

function monthsWord(n: number): string {
  if (n === 1) return "miesiąc";
  const lastTwo = n % 100;
  const last = n % 10;
  return last >= 2 && last <= 4 && (lastTwo < 12 || lastTwo > 14) ? "miesiące" : "miesięcy";
}

/* Legend for the dots next to "Moje faktury B2B" in the sidebar (same colours and
   wording — see ../badges.ts). Always lists all three, so a colour seen in the menu
   can be looked up even when its count here is already 0. */
function B2BBadgeLegend({ counts }: { counts: Record<B2BEmployeeBadge, number> }) {
  return (
    <div className="rounded-md border border-slate-200 bg-white p-4">
      <p className="mb-3 text-sm font-medium text-slate-700">
        Co oznaczają liczniki przy „Moje faktury B2B" w menu
      </p>
      <ul className="space-y-2">
        {B2B_EMPLOYEE_BADGES.map((badge) => (
          <li key={badge} className="flex items-center gap-3 text-sm">
            <span
              className={`min-w-[1.75rem] rounded-full px-1.5 py-0.5 text-center font-mono text-[10px] leading-none ${
                B2B_BADGE_CHIP_CLASSES[B2B_BADGE_COLORS[badge]]
              } ${counts[badge] === 0 ? "opacity-40" : ""}`}
            >
              {counts[badge]}
            </span>
            <span className={counts[badge] === 0 ? "text-slate-400" : "text-slate-700"}>
              {B2B_BADGE_LABELS[badge]}
            </span>
          </li>
        ))}
      </ul>
      <p className="mt-3 text-xs text-slate-500">
        Licznik w menu odświeża się co kilka minut — po dodaniu lub złożeniu faktury
        może jeszcze chwilę pokazywać starą liczbę.
      </p>
    </div>
  );
}

export default function MyB2BInvoicesPage() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  // CEO / księgowość na kontrakcie B2B widzą w API wszystkie faktury — tu tylko własne.
  // Zwykłemu pracownikowi B2B backend i tak zawęża listę, parametr jest ignorowany.
  const { data, isLoading } = useB2BInvoices(user ? { employee: user.id } : undefined);
  const { data: missing } = useMissingB2BInvoicePeriods(true);

  if (isLoading) return <Spinner />;

  const invoices = data?.data ?? [];
  const missingPeriods = missing?.missing_periods ?? [];
  const legendCounts: Record<B2BEmployeeBadge, number> = {
    missing_periods: missingPeriods.length,
    rejected: invoices.filter((inv) => inv.status === "rejected").length,
    drafts: invoices.filter((inv) => inv.status === "draft").length,
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Moje faktury B2B</h1>
        <Link to="/b2b-invoices/new">
          <Button>
            <Plus className="mr-1 h-4 w-4" />
            Dodaj fakturę
          </Button>
        </Link>
      </div>

      <B2BBadgeLegend counts={legendCounts} />

      {missingPeriods.length > 0 && (
        <div className="rounded-md border border-red-200 bg-red-50 p-4">
          <p className="text-sm font-medium text-red-800">
            Brakuje faktury za {missingPeriods.length} {monthsWord(missingPeriods.length)}
          </p>
          <p className="mt-1 text-sm text-red-700">
            Fakturę za miesiąc na umowie B2B trzeba dodać do 5. dnia kolejnego miesiąca
            (miesiące na UoP/UZ rozliczasz kartą godzin; w miesiącu zmiany umowy wystarczy
            jeden dokument — faktura albo, jeśli tak ustalisz z kadrami, karta godzin). Te
            miesiące nie mają jeszcze żadnego z nich, więc nie widać ich w tabeli poniżej —
            liczą się do czerwonego licznika w menu, dopóki ich nie dodasz.
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            {missingPeriods.map(({ year, month }) => (
              <Link
                key={`${year}-${month}`}
                to={`/b2b-invoices/new?period=${year}-${String(month).padStart(2, "0")}`}
                className="inline-flex items-center gap-1 rounded-md border border-red-300 bg-white px-2.5 py-1 text-xs font-medium text-red-800 shadow-sm transition-colors hover:bg-red-100"
              >
                <Plus className="h-3.5 w-3.5" />
                Dodaj fakturę za {MONTH_NAMES[month - 1].toLowerCase()} {year}
              </Link>
            ))}
          </div>
        </div>
      )}

      {invoices.length === 0 ? (
        <div className="rounded-md border border-slate-200 bg-slate-50 p-8 text-center text-sm text-slate-600">
          Nie masz jeszcze żadnych faktur. Kliknij „Dodaj fakturę", aby rozpocząć.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-md border border-slate-200">
          <table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-3 py-2 text-left font-medium">Okres</th>
                <th className="px-3 py-2 text-left font-medium">Numer</th>
                <th className="px-3 py-2 text-left font-medium">Data</th>
                <th className="px-3 py-2 text-right font-medium">Kwota brutto</th>
                <th className="px-3 py-2 text-left font-medium">Status</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 bg-white">
              {invoices.map((inv) => (
                <tr
                  key={inv.id}
                  className="cursor-pointer border-l-2 border-transparent transition-colors hover:border-[var(--color-primary)] hover:bg-slate-50"
                  onClick={() => navigate(`/b2b-invoices/${inv.id}`)}
                >
                  <td className="px-3 py-2">
                    {String(inv.period_month).padStart(2, "0")}.{inv.period_year}
                  </td>
                  <td className="px-3 py-2 font-mono text-xs">{inv.invoice_number}</td>
                  <td className="px-3 py-2">{formatDate(inv.issue_date)}</td>
                  <td className="px-3 py-2 text-right font-medium">
                    {formatPln(inv.amount_gross)}
                  </td>
                  <td className="px-3 py-2">
                    <B2BInvoiceStatusBadge status={inv.status} />
                  </td>
                  <td className="px-3 py-2 text-right">
                    <Link
                      to={`/b2b-invoices/${inv.id}`}
                      onClick={(e) => e.stopPropagation()}
                      className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2.5 py-1 text-xs font-medium text-slate-700 shadow-sm transition-colors hover:border-[var(--color-primary)] hover:bg-slate-50 hover:text-[var(--color-primary)]"
                    >
                      Szczegóły
                      <ChevronRight className="h-3.5 w-3.5" />
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
